/**
 * useTaskCommentAttachments · trae los adjuntos de un set de comentarios y los
 * devuelve agrupados por `owner_id` (comment.id).
 *
 * Reusa la infra de `core.attachments` (owner_kind = 'task_comment', ya en el
 * enum) y el API del chat — no requiere tablas nuevas. Sirve para pintar los
 * chips de adjunto debajo de cada comentario en el timeline.
 */
import { useQuery } from '@tanstack/react-query';

import { listAttachmentsForOwners } from '@/features/chat/api/attachments';
import type { AttachmentsRow } from '@/types/database.types';

export type CommentAttachmentsMap = Map<string, AttachmentsRow[]>;

export function useTaskCommentAttachments(commentIds: string[]) {
  // Key estable: ids ordenados → el cache no se invalida por reordenamientos.
  const sortedIds = [...commentIds].sort();
  return useQuery<CommentAttachmentsMap>({
    queryKey: ['core', 'task_comment_attachments', sortedIds],
    enabled: sortedIds.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const rows = await listAttachmentsForOwners('task_comment', sortedIds);
      const map: CommentAttachmentsMap = new Map();
      for (const row of rows) {
        const arr = map.get(row.owner_id) ?? [];
        arr.push(row);
        map.set(row.owner_id, arr);
      }
      return map;
    },
  });
}
